import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { useT } from '../i18n/useT';
import { scrollToMarketingHash } from '../marketing/marketingHash';
import { VISIME_APP_STORE_URL, VISIME_PLAY_STORE_URL } from '../marketing/storeUrls';

const L = `${process.env.PUBLIC_URL || ''}/marketing-assets`;

const IMG = {
  hero: `${L}/Hero.png`,
  heroDark: `${L}/Hero-dark.png`,
  appPhone: `${L}/VisiMe-app.png`,
  visiVisual: `${L}/Group-15.png`,
  googlePlay: `${L}/google-play-badge_GER.png`,
  appStore: `${L}/Apple-AppStore_Badge.png`,
} as const;

const facts = [
  { value: 'CHF 0', label: 'ME Profil für Suche und Listen, ohne Abo' },
  { value: '3 Monate', label: 'VISI Single zum Start kostenlos' },
  { value: '1 km', label: 'Kürzeste angezeigte Distanz bei GPS-Standort' },
  { value: 'iOS + Android', label: 'In App Store und Google Play' },
] as const;

const visiDefs: { term: string; body: string }[] = [
  {
    term: 'Kanäle',
    body: 'Soziale Netzwerke, Online-Marktplätze, Musik- und Podcast-Dienste, Telefonnummern, E-Mail-Adressen und Webseiten laufen in einem VISI zusammen.',
  },
  {
    term: 'Benutzername',
    body: 'Mit einem eigenen VISI-Benutzernamen wird dein Profil schnell gefunden und lässt sich einfach in dein Marketing einbinden.',
  },
  {
    term: 'Telefonnummer',
    body: 'Verknüpfe deine Nummer mit bekannten Messaging-Diensten. Kontakte erreichen dich direkt, ohne dich vorher im Telefonbuch zu speichern.',
  },
  {
    term: 'Kategorie',
    body: 'Ordne dein VISI einer passenden VISI Kategorie zu, damit die richtigen Personen dich finden.',
  },
  {
    term: 'Standort',
    body: 'Du entscheidest, ob dein VISI an einer festen Adresse oder über deinen GPS-Standort angezeigt wird. Bei GPS-Standorten zeigen wir als kürzeste Entfernung 1 km an, zu deiner Sicherheit.',
  },
  {
    term: 'Sichtbarkeit',
    body: 'Schalte dein VISI in der App jederzeit sichtbar oder unsichtbar, je nachdem, ob du gerade gefunden werden möchtest.',
  },
];

function scrollTo(e: MouseEvent, hash: string) {
  e.preventDefault();
  window.history.replaceState(null, '', `/landing${hash}`);
  scrollToMarketingHash(hash);
}

function StoreBadges() {
  return (
    <div className="l-badges">
      <a className="l-badge-link" href={VISIME_APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="VisiMe im App Store laden">
        <img src={IMG.appStore} alt="Laden im App Store" width={137} height={46} />
      </a>
      <a className="l-badge-link l-badge-link--play" href={VISIME_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="VisiMe bei Google Play laden">
        <img src={IMG.googlePlay} alt="Jetzt bei Google Play" width={119} height={46} />
      </a>
    </div>
  );
}

function idx(i: number): CSSProperties {
  return { ['--i' as string]: i } as CSSProperties;
}

export default function LandingPage() {
  const t = useT();
  const location = useLocation();
  const [yearly, setYearly] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!location.hash) return;
    const timer = window.setTimeout(() => scrollToMarketingHash(location.hash, 'auto'), 80);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const targets = Array.from(root.querySelectorAll<HTMLElement>('.rv'));
    const vh = window.innerHeight;
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top < vh * 0.92) el.classList.add('rv-in');
    });
    root.classList.add('rv-ready');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('rv-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    targets.forEach((el) => {
      if (!el.classList.contains('rv-in')) io.observe(el);
    });
    return () => {
      io.disconnect();
      root.classList.remove('rv-ready');
    };
  }, []);

  const single = { amount: yearly ? '99' : '9.90', unit: yearly ? 'CHF / Jahr' : 'CHF / Monat' };
  const duo = { amount: yearly ? '199' : '19.90', unit: yearly ? 'CHF / Jahr' : 'CHF / Monat' };

  return (
    <MarketingLayout
      headerVariant="hero"
      title="VisiMe — Sichtbarkeit & Beyond"
      description="Digitale Visitenkarten, standortbasierte Sichtbarkeit und verknüpfte Kanäle — VisiMe App für iOS und Android."
    >
      <div ref={rootRef}>
        <section className="l-hero" aria-labelledby="hero-title">
          <div className="l-wrap l-hero-grid">
            <div>
              <p className="l-eyebrow l-hero-in" style={idx(0)}>
                Digitale Visitenkarte
              </p>
              <h1 id="hero-title" className="l-hero-in" style={idx(1)}>
                Sichtbar, wenn du <em>gefunden</em> werden willst.
              </h1>
              <p className="l-lead l-hero-in" style={idx(2)}>
                Ein VISI bündelt Social Media, Telefon, Web und Standort in einem Profil. In der App entscheidest du, wann und wo man dich findet.
              </p>
              <div className="l-hero-actions l-hero-in" style={idx(3)}>
                <StoreBadges />
                <RouterLink to="/waiting-list" className="landing-cta-secondary">
                  {t('waitlistCtaShort')}
                </RouterLink>
              </div>
              <p className="l-hero-note l-hero-in" style={idx(4)}>
                ME Profil kostenlos · VISI 3 Monate gratis testen
              </p>
            </div>
            <div className="l-stage l-hero-in" style={idx(2)}>
              <span className="l-stage-ring l-stage-ring--3" />
              <span className="l-stage-ring l-stage-ring--2" />
              <span className="l-stage-ring l-stage-ring--1" />
              <img className="l-stage-phone l-stage-phone--light" src={IMG.hero} alt="VisiMe App: Feed mit VISI Profilen in der Nähe" />
              <img className="l-stage-phone l-stage-phone--dark" src={IMG.heroDark} alt="" aria-hidden="true" />
              <div className="l-stage-chip">
                <span className="l-dot" aria-hidden="true" />
                <div>
                  <strong>Sichtbar in deiner Nähe</strong>
                  <span>GPS-Standort ab 1 km Distanz</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="l-facts" aria-label="Eckdaten">
          <div className="l-wrap l-facts-grid">
            {facts.map((f, i) => (
              <div key={f.value} className="l-fact rv" style={idx(i)}>
                <span className="l-fact-value">{f.value}</span>
                <span className="l-fact-label">{f.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="l-section l-section--soft" id="app">
          <div className="l-wrap l-split l-split--text-left">
            <div className="rv">
              <p className="l-eyebrow">Die App</p>
              <h2 className="l-h2">Visitenkarte, Marktplatz und Suche in einer App.</h2>
              <p className="l-lead">
                VisiMe ist die Plattform für digitale Visitenkarten und der erste standortbasierte Marktplatz, auf dem du Dienstleistungen und Produkte in VISI Kategorien präsentierst.
              </p>
              <ul className="l-inline-points">
                <li>
                  <b>Marktplatz</b>
                  <span>Unternehmen und Dienstleistungen in VISI Kategorien, mit aktuellen Kontaktwegen.</span>
                </li>
                <li>
                  <b>Probezeit</b>
                  <span>Teste die Erstellung deines VISI Profils direkt in der App.</span>
                </li>
                <li>
                  <b>Kein Druck</b>
                  <span>Kontaktdaten pflegst du zentral. Neu drucken musst du nichts.</span>
                </li>
              </ul>
              <a className="l-link" href="/landing#visi" onClick={(e) => scrollTo(e, '#visi')}>
                VISI entdecken <ArrowForwardIcon sx={{ fontSize: 18 }} />
              </a>
            </div>
            <div className="l-panel l-panel--app rv" style={idx(1)}>
              <img src={IMG.appPhone} alt="VisiMe App: Favoriten-Gruppen und Kontoeinstellungen" loading="lazy" />
            </div>
          </div>
        </section>

        <section className="l-section" id="visi">
          <div className="l-wrap l-split l-split--text-right" style={{ alignItems: 'start' }}>
            <div className="l-panel l-panel--wash l-panel--duo rv" style={{ position: 'sticky', top: 104 }}>
              <img src={IMG.visiVisual} alt="VisiMe App: VISI Feed und Favoriten-Gruppen" loading="lazy" />
            </div>
            <div className="rv" style={idx(1)}>
              <p className="l-eyebrow">VISI</p>
              <h2 className="l-h2">Dein öffentliches Profil, einmal gepflegt.</h2>
              <p className="l-lead">Alles, was Kunden von dir brauchen, an einer Adresse. Ändern sich Daten, passt du sie im VISI an. Fertig.</p>
              <dl className="l-defs">
                {visiDefs.map((d) => (
                  <div key={d.term} className="l-def">
                    <dt>{d.term}</dt>
                    <dd>{d.body}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="l-night" id="me">
          <div className="l-wrap">
            <div className="l-night-head rv">
              <div>
                <p className="l-eyebrow">ME</p>
                <h2 className="l-h2">Dein privates Profil zum Suchen und Merken.</h2>
              </div>
              <p className="l-lead">Mit deinem ME Profil suchst du VISI Profile und speicherst sie in Listen. Niemand sonst sieht es.</p>
            </div>
            <div className="l-bento">
              <article className="l-tile l-bento-a rv">
                <h3>Nicht öffentlich sichtbar</h3>
                <p>Dein ME Profil ist nur für dich da. Sichtbar bist du ausschliesslich über dein VISI, und dort bestimmst du selbst.</p>
                <div className="l-tile-demo">
                  <div className="l-vis" aria-hidden="true">
                    <div className="l-vis-row">
                      <div>
                        ME Profil
                        <small>privat</small>
                      </div>
                      <span className="l-switch" />
                    </div>
                    <div className="l-vis-row">
                      <div>
                        VISI Profil
                        <small>öffentlich, wenn du es willst</small>
                      </div>
                      <span className="l-switch l-switch--on" />
                    </div>
                    <div className="l-vis-row">
                      <div>
                        GPS-Standort
                        <small>Anzeige ab 1 km Distanz</small>
                      </div>
                      <span className="l-switch l-switch--on" />
                    </div>
                  </div>
                </div>
              </article>
              <article className="l-tile rv" style={idx(1)}>
                <h3>Lieblingskategorien</h3>
                <p>Baue dir in der App eine persönliche Home-Seite mit deinen bevorzugten VISI Kategorien.</p>
                <div className="l-tile-demo">
                  <div className="l-chips" aria-hidden="true">
                    <span className="l-chip l-chip--on">Design</span>
                    <span className="l-chip l-chip--on">Computer</span>
                    <span className="l-chip">Beauty</span>
                  </div>
                </div>
              </article>
              <article className="l-tile rv" style={idx(2)}>
                <h3>Die optimale Suche</h3>
                <p>Simuliere einen anderen ME Standort, um gezielt dort nach VISI Profilen zu suchen.</p>
                <div className="l-tile-demo">
                  <div className="l-route" aria-hidden="true">
                    <span className="l-route-pin">Dein Standort</span>
                    <span className="l-route-line" />
                    <span className="l-route-pin l-route-pin--active">Simuliert</span>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="l-section l-section--soft" id="preisplan">
          <div className="l-wrap l-pricing">
            <div className="l-pricing-side rv">
              <p className="l-eyebrow">Preisplan</p>
              <h2 className="l-h2">Starte kostenlos, veröffentliche, wenn du bereit bist.</h2>
              <div className="l-toggle" role="group" aria-label="Abrechnungszeitraum">
                <button type="button" aria-pressed={!yearly} onClick={() => setYearly(false)}>
                  Monatlich
                </button>
                <button type="button" aria-pressed={yearly} onClick={() => setYearly(true)}>
                  Jährlich
                </button>
              </div>
              <p className="l-price-note">Alle Preise in CHF. VISI Single startet mit 3 Gratismonaten.</p>
            </div>
            <div className="l-plans">
              <article className="l-plan rv">
                <div>
                  <h3 className="l-plan-name">ME</h3>
                  <p className="l-plan-desc">Für die Suche, Listen und dein privates Profil.</p>
                  <ul className="l-plan-lines">
                    <li>Nicht öffentlich sichtbar</li>
                    <li>Personalisiertes HOME</li>
                    <li>Integrierte Suche</li>
                    <li>Standort simulieren</li>
                    <li>Listen für VISI</li>
                  </ul>
                </div>
                <div className="l-plan-price">
                  <span className="l-plan-amount">Gratis</span>
                  <span className="l-plan-unit">dauerhaft</span>
                </div>
              </article>

              <article className="l-plan l-plan--featured rv" style={idx(1)}>
                <div>
                  <h3 className="l-plan-name">
                    VISI Single
                    {!yearly && <span className="l-tag">3 Monate gratis</span>}
                  </h3>
                  <p className="l-plan-desc">Ein öffentliches VISI mit allen Verknüpfungen.</p>
                  <ul className="l-plan-lines">
                    <li>VISI öffentlich sichtbar</li>
                    <li>Eindeutiger Benutzername</li>
                    <li>Kategorie und Standort</li>
                    <li>Soziale Netzwerke</li>
                    <li>Sichtbarkeit steuern</li>
                  </ul>
                </div>
                <div className="l-plan-price">
                  <span className="l-plan-amount">{single.amount}</span>
                  <span className="l-plan-unit">{single.unit}</span>
                </div>
              </article>

              <article className="l-plan rv" style={idx(2)}>
                <div>
                  <h3 className="l-plan-name">VISI Duo</h3>
                  <p className="l-plan-desc">Zwei VISI gleichzeitig veröffentlichen.</p>
                  <ul className="l-plan-lines">
                    <li>Alle Vorteile von VISI Single</li>
                    <li>Bis zu zwei VISI</li>
                  </ul>
                </div>
                <div className="l-plan-price">
                  <span className="l-plan-amount">{duo.amount}</span>
                  <span className="l-plan-unit">{duo.unit}</span>
                </div>
              </article>

              <article className="l-plan rv" style={idx(3)}>
                <div>
                  <h3 className="l-plan-name">VISI Promoter</h3>
                  <p className="l-plan-desc">Reichweite über Promoter-Standorte.</p>
                  <ul className="l-plan-lines">
                    <li>Mehrere Standorte</li>
                    <li>Eindeutiger Benutzername</li>
                    <li>Sichtbarkeit durch Eigentümer</li>
                  </ul>
                </div>
                <div className="l-plan-price">
                  <span className="l-plan-amount">Individuell</span>
                  <span className="l-plan-unit">auf Anfrage</span>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="l-download" id="download">
          <div className="l-wrap">
            <div className="l-download-panel rv">
              <div className="l-download-rings" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </div>
              <div>
                <p className="l-eyebrow">Jetzt loslegen</p>
                <h2>In wenigen Minuten sichtbar.</h2>
                <p>Lade VisiMe, lege dein ME Profil an und teste dein erstes VISI drei Monate kostenlos.</p>
              </div>
              <div className="l-download-actions">
                <StoreBadges />
                <RouterLink to="/waiting-list" className="landing-cta-secondary">
                  {t('waitlistCtaLong')}
                </RouterLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
